import { isDefined } from "@clipboard-health/util-ts";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useRef } from "react";

import { useCreateOrGetPlacementCandidate } from "../../PlacementCandidate/api/useCreateOrGetPlacementCandidate";
import { useIsJobsEnabled } from "../../PlacementCandidate/useIsJobsEnabled";
import { useFetchPaginatedPlacements } from "../../Placements/api/useFetchPaginatedPlacements";
import { useGetPlacementFilters } from "../../Placements/components/PlacementDetailsPage/hooks/useGetPlacementFilters";

interface UseWorkplaceStaffJobsDataResult {
  isJobsEnabled: boolean;
  isLoading: boolean;
  placementsCount: number;
  placementCandidateId: string | undefined;
}

export function useWorkplaceStaffJobsData(workplaceId: string): UseWorkplaceStaffJobsDataResult {
  const isJobsEnabled = useIsJobsEnabled();
  const worker = useDefinedWorker();

  const { data: placementCandidate, isError: isPlacementCandidateError } =
    useCreateOrGetPlacementCandidate(
      { workerId: worker.userId },
      { enabled: isJobsEnabled && isDefined(worker.userId) }
    );

  const placementFilters = useGetPlacementFilters({
    filter: { workplaceId },
    worker,
    enabled: isJobsEnabled && isDefined(worker.userId) && isDefined(placementCandidate),
  });

  const isPlacementsQueryEnabled =
    isJobsEnabled &&
    isDefined(worker.userId) &&
    isDefined(placementCandidate) &&
    isDefined(placementFilters);

  const { data: placementsData, isError: isPlacementsError } = useFetchPaginatedPlacements(
    {
      workerId: worker.userId,
      placementCandidateId: placementCandidate?.id ?? "",
      filter: placementFilters,
      shouldLogEvent: false,
    },
    {
      enabled: isPlacementsQueryEnabled,
    }
  );

  const rawPlacementsCount =
    placementsData?.pages.reduce((count, page) => count + page.data.length, 0) ?? 0;

  const hasError = isPlacementCandidateError || isPlacementsError;
  const hasResolvedNow = !isJobsEnabled || hasError || isDefined(placementsData);

  const hasResolvedRef = useRef(hasResolvedNow);
  if (hasResolvedNow) {
    hasResolvedRef.current = true;
  }

  const latchedCountRef = useRef(rawPlacementsCount);
  if (isDefined(placementsData)) {
    latchedCountRef.current = rawPlacementsCount;
  }

  const placementsCount = isDefined(placementsData) ? rawPlacementsCount : latchedCountRef.current;
  const isLoading = !hasResolvedRef.current;

  return {
    isJobsEnabled,
    isLoading,
    placementsCount,
    placementCandidateId: placementCandidate?.id,
  };
}
